import { getAssetTypeAutoAdjust } from './get-asset-type-auto-adjust';

export interface AssetTypeChangeInput {
  newType: string;
  /** The row's behaviors as they will stand when the plan is applied. */
  behaviors: readonly string[] | null | undefined;
  /** The row's Rich Asset Content as it will stand when the plan is applied. */
  richAssetContent: unknown;
  /** `tableStore.richAssetsConfig`: the default content per asset type. */
  richAssetsConfig: object;
}

export interface AssetTypeChangePlan {
  /**
   * The columns to write with the new type. Only the type is always present;
   * a dependent column appears only when the change actually needs it.
   */
  updates: {
    richAssetType: string;
    behaviors?: string[];
    nluDisabled?: boolean;
    richAssetContent?: string;
  };
  /** Which dependent cells `updates` changes, so a caller can clear their stale errors. */
  adjusted: {
    behaviors: boolean;
    nluDisabled: boolean;
    richAssetContent: boolean;
  };
}

/**
 * PSTUDIO-4348: what changing a row's Rich Asset Type writes, as data. ONE
 * definition for the entrances that change the type through an editor or a
 * paste — the cell dropdown and the Expanded Cell View panel (both through
 * `commitAssetTypeChange`), a pasted cell, a cell-range paste and pasted TSV
 * rows — so a pasted type cannot leave the row with a type/content mismatch that
 * the dropdown would have prevented. Clearing the cell (the source of a cut,
 * Delete/Backspace) does not go through it; rail 19 of
 * `KEYBOARD_CLIPBOARD_BEHAVIOR.md` names that limit.
 *
 *  - behaviors / nluDisabled: `getAssetTypeAutoAdjust` (PSTUDIO-3422).
 *  - richAssetContent: seeded with the type's default, only while the content is
 *    empty — content the row already has is never replaced.
 *
 * Pure: the caller supplies the values the row will hold, so a range paste that
 * also writes behaviors or content in the same fill plans against THOSE values.
 */
export function planAssetTypeChange(
  input: AssetTypeChangeInput
): AssetTypeChangePlan {
  const { newType, behaviors, richAssetContent, richAssetsConfig } = input;
  const autoAdjust = getAssetTypeAutoAdjust(newType, behaviors);

  const updates: AssetTypeChangePlan['updates'] = { richAssetType: newType };
  if (autoAdjust.behaviors) updates.behaviors = autoAdjust.behaviors;
  if (autoAdjust.nluDisabled !== undefined) {
    updates.nluDisabled = autoAdjust.nluDisabled;
  }

  // Own keys only, like the type map in the converter: the config is a plain
  // object, and `constructor` would otherwise read as a "default content".
  const defaultContent = Object.prototype.hasOwnProperty.call(
    richAssetsConfig,
    newType
  )
    ? (richAssetsConfig as Record<string, unknown>)[newType]
    : undefined;
  const seedsContent = !richAssetContent && defaultContent !== undefined;
  if (seedsContent) {
    updates.richAssetContent =
      typeof defaultContent === 'string'
        ? defaultContent
        : JSON.stringify(defaultContent, null, 2);
  }

  return {
    updates,
    adjusted: {
      behaviors: autoAdjust.behaviors !== undefined,
      nluDisabled: autoAdjust.nluDisabled !== undefined,
      richAssetContent: seedsContent,
    },
  };
}
